import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import Sidebar, { DRAWER_WIDTH } from './Sidebar';
import ErrorBoundary from '../components/ErrorBoundary';
import { getAdminRole } from '../utils/auth';

// 경로 → 페이지 제목 매핑 (AppBar 우측 표시용)
const PAGE_TITLES = {
  '/': '대시보드',
  '/notices': '공지사항',
  '/faqs': 'FAQ',
  '/banners': '배너 관리',
  '/members': '사용자 관리',
  '/subscriptions': '구독 관리',
  '/settings': '판매정책',
  '/versions': '앱 버전',
  '/sales': '매출 관리',
  '/usage': '이용 관리',
  '/camera': '카메라',
  '/lpr-test': 'LPR 테스트',
  '/lpr-logs': '인식 로그',
  '/kiosk': '키오스크',
  '/admins': '계정 관리',
};

function getPageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  // /members/abc → /members 매칭
  const base = '/' + pathname.split('/')[1];
  return PAGE_TITLES[base] || '';
}

const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // 1200px(lg) 미만에서 햄버거 메뉴 표시 (Sidebar와 같은 breakpoint)
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleDrawerClose = () => setMobileOpen(false);

  // role 은 JWT 에서 (localStorage 변조 방지)
  const role = getAdminRole();
  const isMaster = role === 'master';
  const pageTitle = getPageTitle(location.pathname);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminStoreId');
    localStorage.removeItem('adminStoreCode');
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* 모바일/태블릿: 상단 AppBar + 햄버거 */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            // 부드러운 그라데이션 + 하단 라인
            background: 'linear-gradient(135deg, #1a1a2e 0%, #2a2a5e 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            zIndex: (t) => t.zIndex.drawer + 1,
            // glassmorphism 느낌
            backdropFilter: 'saturate(180%) blur(8px)',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2 } }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 1,
                borderRadius: '8px',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
              aria-label="메뉴 열기"
            >
              <MenuIcon />
            </IconButton>

            {/* 로고 + 브랜드명 — 클릭 시 대시보드로 */}
            <Box
              onClick={() => navigate('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                borderRadius: '8px',
                px: 1,
                py: 0.5,
                ml: -1,
                transition: 'background 0.15s',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <LocalCarWashIcon sx={{ fontSize: 22, color: '#E91E63' }} />
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg, #fff 0%, #c5c5e0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Mogoo
              </Typography>
            </Box>

            {/* 가운데: 현재 페이지명 */}
            {pageTitle && (
              <Typography
                variant="body1"
                sx={{
                  ml: 2,
                  pl: 2,
                  borderLeft: '1px solid rgba(255,255,255,0.15)',
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 600,
                  fontSize: { xs: 14, sm: 15 },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexGrow: 1,
                }}
              >
                {pageTitle}
              </Typography>
            )}

            {/* 우측: 권한 chip + 로그아웃 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <Chip
                label={isMaster ? 'MASTER' : 'MANAGER'}
                size="small"
                sx={{
                  height: 24,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  backgroundColor: isMaster ? '#E91E63' : '#1976d2',
                  color: '#fff',
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
              <Tooltip title="로그아웃" arrow>
                <IconButton
                  color="inherit"
                  onClick={handleLogout}
                  size="small"
                  sx={{
                    borderRadius: '8px',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                  }}
                  aria-label="로그아웃"
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Sidebar mobileOpen={mobileOpen} onMobileClose={handleDrawerClose} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // AppBar(56~64px) + 시각적 호흡 여백
          // pt 11 (88px) = AppBar 64px + 24px 호흡
          p: { xs: 2, lg: 3 },
          pt: { xs: 11, sm: 12, lg: 3 },
          width: { xs: '100%', lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,  // flex 자식 가로 스크롤 방지
        }}
      >
        {/* 페이지 단위 에러 격리 — 한 페이지가 터져도 사이드바는 살아있음 */}
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
};

export default MainLayout;
